// packages/core/src/learning/schools/_validate.ts
// ─────────────────────────────────────────────────────────────────────────
// Runtime validator for authored school exhibits. console.warn ONLY — it
// never throws and never blocks rendering. A broken or incomplete exhibit
// still renders best-effort (and falls back to the lean SchoolMeta view for
// anything truly missing).
//
// WHY THIS EXISTS / WHAT IT DOES NOT DO:
//   TypeScript already enforces the *typed* fields at COMPILE time:
//     - figures: FigureId[]        → an unknown figure id won't compile
//     - applyActions[].action      → must match the ApplyAction union
//     - scenarioType               → must be a valid ScenarioKind
//   So this validator does NOT re-check those. It focuses on the gaps the
//   compiler can't see at runtime:
//     1. featuredLessonIds that don't resolve to a real lesson  (string[])
//     2. debateIds that don't resolve to a real debate           (string[])
//     3. nativeModuleId set but referencing nothing recognizable
//     4. required editorial content present but EMPTY (blank tagline, no
//        beliefs, empty worldview, zero canon, etc.)
//     5. id mismatch (file says one id, keyed under another)
//     6. ListedSchools that have SchoolMeta but NO exhibit yet (the live
//        "still to author" to-do list)
//
// Every warning is prefixed `[school-exhibits]` so they're easy to filter
// in the console.
// ─────────────────────────────────────────────────────────────────────────

import type { ListedSchool } from '../LearningTypes';
import type { SchoolExhibitEntry } from '../SchoolExhibitMeta';
import { LISTED_SCHOOLS, getSchoolMeta } from '../SchoolMeta';
import { getLessonById } from '../index';
import { getDebate } from '../DebateMeta';

type ExhibitMap = Partial<Record<ListedSchool, SchoolExhibitEntry>>;

declare const console: { warn(...args: unknown[]): void };
const PREFIX = '[school-exhibits]';

function warn(school: string, msg: string): void {
	 
	console.warn(`${PREFIX} ${school}: ${msg}`);
}

/** True if a string is non-empty after trimming. */
function filled(s: unknown): boolean {
	return typeof s === 'string' && s.trim().length > 0;
}

/** Validate a single authored exhibit. console.warn-only; never throws.
 *  `blindSpot` is OPTIONAL and intentionally out of scope — its absence is not
 *  content debt and is never reported here. */
function validateOne(id: ListedSchool, ex: SchoolExhibitEntry): void {
	// 5 · id integrity ----------------------------------------------------
	if (ex.id !== id) {
		warn(id, `entry.id is "${ex.id}" but it is keyed under "${id}"`);
	}

	// 4 · required editorial content present-but-empty --------------------
	if (!filled(ex.tagline)) warn(id, 'tagline is empty');
	if (!filled(ex.worldviewSummary)) warn(id, 'worldviewSummary is empty');
	if (!Array.isArray(ex.coreBeliefs) || ex.coreBeliefs.length === 0) {
		warn(id, 'coreBeliefs is empty (expected 3–7)');
	} else if (ex.coreBeliefs.some((b) => !filled(b))) {
		warn(id, 'coreBeliefs contains a blank entry');
	}
	if (!Array.isArray(ex.canon) || ex.canon.length === 0) {
		warn(id, 'canon is empty (expected 3–5)');
	} else {
		ex.canon.forEach((c, i) => {
			if (!filled(c.title)) warn(id, `canon[${i}].title is empty`);
			if (!filled(c.oneLineIdea))
				warn(id, `canon[${i}].oneLineIdea is empty`);
		});
	}
	if (!Array.isArray(ex.applyActions) || ex.applyActions.length === 0) {
		warn(id, 'applyActions is empty (expected at least 1)');
	}
	if (!Array.isArray(ex.marketingHooks) || ex.marketingHooks.length === 0) {
		warn(id, 'marketingHooks is empty');
	}
	if (typeof ex.revision !== 'number' || ex.revision < 1) {
		warn(id, 'revision should be a number ≥ 1');
	}

	// 1 · dangling featured lesson ids ------------------------------------
	(ex.featuredLessonIds ?? []).forEach((lid) => {
		if (!filled(lid)) {
			warn(id, 'featuredLessonIds contains a blank id');
			return;
		}
		if (!getLessonById(lid)) {
			warn(id, `featuredLessonId "${lid}" does not resolve to a lesson`);
		}
	});

	// 2 · dangling debate ids ---------------------------------------------
	(ex.debateIds ?? []).forEach((did) => {
		if (!filled(did)) {
			warn(id, 'debateIds contains a blank id');
			return;
		}
		if (!getDebate(did)) {
			warn(id, `debateId "${did}" does not resolve to a debate`);
		}
	});

	// 3 · apply-action lessonId back-references (optional but if set, real) -
	(ex.applyActions ?? []).forEach((a, i) => {
		if (a.lessonId !== undefined && !getLessonById(a.lessonId)) {
			warn(
				id,
				`applyActions[${i}].lessonId "${a.lessonId}" does not resolve to a lesson`,
			);
		}
	});

	// blindSpot is OPTIONAL and intentionally out of scope — its absence is not
	// content debt and is never reported. Renders in the Featured lens only when
	// authored; otherwise silently omitted.
}

/**
 * Validate the whole assembled exhibit map. Called once on module load from
 * SchoolExhibitMeta.ts. Safe to call repeatedly.
 */
export function validateExhibits(map: ExhibitMap): void {
	// Per-entry checks. console.warn-only; never throws.
	(Object.keys(map) as ListedSchool[]).forEach((id) => {
		const ex = map[id];
		if (ex) validateOne(id, ex);
	});

	// 6 · the live to-do list: schools with meta but no exhibit yet.
	const authored = new Set(Object.keys(map));
	const missing = LISTED_SCHOOLS.filter((id) => !authored.has(id));
	if (missing.length > 0) {
		// One grouped warning so the console isn't flooded — this is the
		// "still to author" dashboard.
		 
		console.warn(
			`${PREFIX} ${missing.length} school(s) have SchoolMeta but no exhibit yet: ${missing
				.map((m) => `"${m}"`)
				.join(', ')}`,
		);
		// Sanity: every missing id should still have valid meta.
		missing.forEach((id) => {
			if (!getSchoolMeta(id)) {
				warn(
					id,
					'listed school is missing BOTH exhibit and SchoolMeta',
				);
			}
		});
	}
}
